import React, { useState, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import * as XLSX from 'xlsx';
import { db, auth } from '../config/firebase';
import { doc, writeBatch, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { formatSaldo } from '../utils/formatters';
import { SortIcon } from './Icons';
import { theme } from '../config/theme';
import InstructionsModal from './InstructionsModal';
import UploadSnapshotModal from './UploadSnapshotModal';
import SaldoDesgloseModal from './SaldoDesgloseModal';
import ConciliacionResultadoModal from './ConciliacionResultadoModal';
import AddA3MovementModal from './AddA3MovementModal';
import IndicatorCard from './saldos/IndicatorCard';
import AcreditacionesEsperadasAviso from './saldos/AcreditacionesEsperadasAviso';
import {
    marcarAcreditacion,
    solicitarRecalculoAcreditaciones,
} from '../services/acreditacionesEsperadasService';
import { conSigno, formatIsoDay, resumirPorBucket } from '../utils/acreditacionesEsperadas';
import {
    crearSnapshotSaldos,
    formatSnapshotHora,
    getFechaSnapshotKey,
    guardarResultadoConciliacion,
    solicitarCargaSaldosCustodio,
    SNAPSHOT_TIPO,
} from '../services/saldosService';
import { toArrayConSaldo, conciliarSnapshots } from '../utils/balanceCalculator';
import { filterByComitentes } from '../utils/saldosScope';
import {
    BUCKET_ARS,
    MONEDA_BUCKETS,
    MONEDA_MOVIMIENTO,
    MONEDA_TITULO,
    SALDO_INICIAL_FIELD,
    SNAPSHOT_SALDO_FIELD,
    DIFERENCIAS_FIELD,
    bucketDeUnidadCustodio,
} from '../constants/monedas';
import { formatDate, fromIsoDate, toIsoDate } from '../utils/dateHelpers';
import { useSaldosClientes } from '../hooks/useSaldosClientes';
import { useHorizontalScrollSync } from '../hooks/useHorizontalScrollSync';
import { useSaldosCustodioMeta } from '../hooks/useSaldosCustodioMeta';
import TopScrollbar from './TopScrollbar';
import StackedTable from './common/StackedTable';
import MobileCollapsibleSection from './common/MobileCollapsibleSection';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);
// --- Styled Components ---
const TabWrapper = styled.div`
  font-family: ${props => props.theme.typography.fontFamily};
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;
const Title = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 2rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;
const ActionsContainer = styled.div`
  display: flex;
  gap: 1rem;
`;
const Button = styled.button`
  display: inline-block;
  font-family: ${props => props.theme.typography.fontFamily};
  background-color: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.textPrimary};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover { background-color: #333; }
`;
const HiddenInput = styled.input`
  display: none;
`;
const SnapshotBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: rgba(26, 40, 62, 0.08);
  border: 1px solid ${props => props.theme.colors.lightGray};
  color: ${props => props.theme.colors.secondary};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.5rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 500;
  strong { text-transform: capitalize; color: ${props => props.theme.colors.primary}; }
`;
const SnapshotBadgeCount = styled.span`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textPrimary};
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
`;
const IndicatorsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;
const ChartContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;
const ChartWrapper = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: 1.5rem;
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
  text-align: center;
  h3 {
    font-family: ${props => props.theme.typography.titleFamily};
    color: ${props => props.theme.colors.secondary};
    margin-top: 0;
  }
`;
const DoughnutWrapper = styled.div`
  max-width: 280px;
  margin: 0 auto;
`;
const StaleWarning = styled.div`
  background-color: #FEF3C7;
  border: 1px solid #FDE68A;
  color: #92400E;
  padding: 0.75rem 1rem;
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: 1.5rem;
  font-weight: 600;
`;
const CustodioError = styled.div`
  background-color: #FEE2E2;
  border: 1px solid #FECACA;
  color: #991B1B;
  padding: 0.75rem 1rem;
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: 1.5rem;
  font-weight: 600;
`;
const Placeholder = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: 4rem;
  border-radius: ${props => props.theme.borderRadius};
  text-align: center;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  box-shadow: ${props => props.theme.shadows.soft};
`;
const TableContainer = styled.div`
  overflow-x: auto;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
  max-height: 600px;
  overflow-y: auto;

  /* En el celular las tarjetas siguen el scroll de la página: un scroll
     dentro de otro es incómodo con el dedo. */
  @media (max-width: 768px) {
    max-height: none;
    overflow-y: visible;
  }
`;
const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  font-family: ${props => props.theme.typography.bodyFamily};
  font-size: 0.9rem;
  min-width: 240px;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;
const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    th, td {
        padding: 0.75rem 1rem;
        text-align: left;
        border-bottom: 1px solid ${props => props.theme.colors.lightGray};
    }
    th {
        background-color: #F9FAFB;
        font-weight: 600;
        cursor: pointer;
        user-select: none;
        &:hover {
            background-color: #F3F4F6;
        }
    }
    td {
        color: ${props => props.theme.colors.text};
    }
    tr:last-child td {
        border-bottom: none;
    }
`;
const SubTabContainer = styled.div`
  margin-bottom: 2rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
`;
const SubTabButton = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border: none;
  background-color: transparent;
  cursor: pointer;
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border-bottom: 3px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  margin-bottom: -1px;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

// Fila de saldos iniciales en cero para un comitente nuevo del archivo del
// custodio: una clave por moneda seguida, así agregar una no rompe la carga.
const saldosInicialesVacios = () =>
  MONEDA_BUCKETS.reduce((acc, bucket) => ({ ...acc, [SALDO_INICIAL_FIELD[bucket]]: 0 }), {});

const EsperadaPendientes = styled.span`
  display: inline-block;
  margin-left: 0.4rem;
  background-color: rgba(245, 158, 11, 0.18);
  color: #92400E;
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 700;
  font-style: normal;
`;

// Mismo tono que la etiqueta ACREDITACION del desglose.
const EsperadaAcreditadas = styled(EsperadaPendientes)`
  background-color: rgba(20, 184, 166, 0.15);
  color: #115E59;
`;

const colorMovimiento = monto => (monto >= 0 ? theme.colors.success.replace('0.15', '1') : theme.colors.errorText);

const TIPO_MOVIMIENTO_LABEL = {
  caucion: 'Cauciones',
  pase: 'Pases',
  operacion: 'Operaciones',
  instruccion: 'Instrucciones',
  acreditacion: 'Acreditaciones',
  licitacion: 'Licitaciones',
};

// Tooltip de "Mov. Ejecutados": la celda es un neto, y un cupón acreditado el mismo
// día que se debita una licitación queda escondido adentro del número.
const detalleMovimientosEjecutados = (movimientos, moneda) => {
  const porTipo = new Map();
  movimientos.forEach(({ tipo, monto }) => {
    const acc = porTipo.get(tipo) || { cantidad: 0, total: 0 };
    porTipo.set(tipo, { cantidad: acc.cantidad + 1, total: acc.total + monto });
  });
  if (porTipo.size === 0) return undefined;
  const lineas = Array.from(porTipo, ([tipo, { cantidad, total }]) =>
    `${TIPO_MOVIMIENTO_LABEL[tipo] || tipo} (${cantidad}): ${conSigno(total, moneda)}`);
  return [...lineas, 'Click en la fila para ver cada movimiento.'].join('\n');
};

// --- Sub-Components ---

// Total esperado de la celda. Los dollar linked sin importe real suman su estimado
// en pesos ("≈"); si alguno ni siquiera tiene estimado, se avisa "+ linked".
const textoAcreditacionEsperada = (resumen, moneda) => {
  const { total, haySinMonto, estimadoLinked = 0, linkedSinEstimar = 0 } = resumen;
  if (!haySinMonto) return formatSaldo(total, moneda);
  const conEstimado = estimadoLinked !== 0 ? `≈ ${formatSaldo(total + estimadoLinked, moneda)}` : null;
  if (linkedSinEstimar === 0) return conEstimado || formatSaldo(total, moneda);
  if (conEstimado) return `${conEstimado} + linked`;
  return total !== 0 ? `${formatSaldo(total, moneda)} + linked` : 'Linked';
};

// Acreditación esperada del día en la tabla de saldos. Mientras quede algo por
// resolver muestra lo pendiente, en tono informativo (no el verde/rojo de saldo)
// porque es una estimación a corroborar. Resuelta, muestra lo que se acreditó: ya
// está sumado en el Saldo Real, pero ahí se netea con el resto de los movimientos.
const AcreditacionEsperadaCell = ({ resumen, moneda }) => {
  if (!resumen) {
    return <td style={{ textAlign: 'right', color: theme.colors.textSecondary }}>—</td>;
  }
  if (resumen.pendientes === 0 && resumen.acreditadas > 0) {
    const resto = resumen.eventos.length - resumen.acreditadas;
    return (
      <td
        style={{ textAlign: 'right', color: colorMovimiento(resumen.acreditado), whiteSpace: 'nowrap' }}
        title={`Acreditado: ya está sumado en el Saldo Real y el Saldo Final (en Mov. Ejecutados, neto con el resto del día).${resto > 0 ? ` Otras ${resto} resueltas sin importe en el saldo (No llegó).` : ''}`}
      >
        {conSigno(resumen.acreditado, moneda)}
        <EsperadaAcreditadas>{resumen.acreditadas} acred.</EsperadaAcreditadas>
      </td>
    );
  }
  if (resumen.pendientes === 0) {
    return (
      <td
        style={{ textAlign: 'right', color: theme.colors.textSecondary, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
        title="Ya se resolvieron todas y ninguna sumó al saldo (No llegó)."
      >
        Resueltas ({resumen.eventos.length})
      </td>
    );
  }
  const { pendiente } = resumen;
  const cartera = resumen.fechaCartera ? ` al ${formatIsoDay(resumen.fechaCartera)}` : '';
  const parcial = pendiente.hayParcial
    ? ' Incluye letras sin cronograma del proveedor: solo el capital, sin el interés (≥).'
    : '';
  const linked = pendiente.haySinMonto
    ? ' Incluye dollar linked: se acreditan en pesos al A3500; el monto es estimado al dólar mayorista (≈) y al marcarlo Acreditada se carga el importe real.'
    : '';
  const yaAcreditado = resumen.acreditadas > 0
    ? ` Ya acreditado: ${conSigno(resumen.acreditado, moneda)}, sumado en el Saldo Real.`
    : '';
  return (
    <td
      style={{ textAlign: 'right', color: theme.colors.primary, fontStyle: 'italic', whiteSpace: 'nowrap' }}
      title={`Pendiente de corroborar, según la cartera del SGC${cartera}. Al marcarla Acreditada en el desglose pasa al Saldo Real y al Saldo Final.${parcial}${linked}${yaAcreditado}`}
    >
      {pendiente.hayParcial && '≥ '}
      {textoAcreditacionEsperada(pendiente, moneda)}
      <EsperadaPendientes>{resumen.pendientes} pend.</EsperadaPendientes>
    </td>
  );
};

// Vista de tarjetas en el celular (ver useStackedTable).
const SNAPSHOTS_STACK = { title: 'Hora' };
const A3_STACK = { title: 'Cliente' };
const SALDOS_STACK = { title: 'Nombre Cliente' };

const SnapshotsHistorial =({ snapshots = [], onVerConciliacion }) => {
  if (snapshots.length === 0) {
    return <Placeholder>No hay snapshots cargados hoy.</Placeholder>;
  }
  return (
    <TableContainer>
      <StyledTable as={StackedTable} stack={SNAPSHOTS_STACK}>
        <thead>
          <tr>
            <th>Hora</th>
            <th>Tipo</th>
            <th>Subido por</th>
            <th style={{ textAlign: 'right' }}>Cuentas</th>
            {MONEDA_BUCKETS.map(bucket => (
              <th key={bucket} style={{ textAlign: 'right' }}>Dif. {MONEDA_TITULO[bucket]}</th>
            ))}
            <th>Conciliación</th>
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s) => {
            const diferencias = MONEDA_BUCKETS.map(bucket => ({
              bucket,
              cantidad: s.conciliacion?.[DIFERENCIAS_FIELD[bucket]]?.length || 0,
            }));
            const tieneConciliacion = !!s.conciliacion;
            return (
              <tr key={s.id}>
                <td>{formatSnapshotHora(s.uploadedAt)}</td>
                <td style={{ textTransform: 'capitalize' }}><strong>{s.tipo}</strong></td>
                <td>{s.uploadedBy?.nombre || s.uploadedBy?.email || '-'}</td>
                <td style={{ textAlign: 'right' }}>{s.totalCuentas ?? s.comitentes?.length ?? 0}</td>
                {diferencias.map(({ bucket, cantidad }) => (
                  <td
                    key={bucket}
                    style={{ textAlign: 'right', color: cantidad > 0 ? '#B45309' : '#065F46', fontWeight: 600 }}
                  >
                    {cantidad}
                  </td>
                ))}
                <td>
                  {tieneConciliacion ? (
                    <button
                      onClick={() => onVerConciliacion(s)}
                      style={{
                        background: 'none',
                        border: '1px solid #1A283E',
                        borderRadius: '4px',
                        padding: '0.3rem 0.7rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#1A283E',
                      }}
                    >
                      Ver detalle
                    </button>
                  ) : (
                    <span style={{ color: '#465163', fontSize: '0.8rem', fontStyle: 'italic' }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

// --- Main Component ---
/*
 * En modo productor (el proveedor de saldos acota las lecturas a sus comitentes)
 * la pestaña es de solo lectura: la carga del archivo del custodio, los
 * movimientos de títulos A3 y el historial de snapshots son de la mesa, y la
 * proyección que lee el productor no trae ni quién subió el snapshot ni la
 * conciliación. Ve los mismos saldos calculados, pero solo de sus clientes.
 *
 * `filtroMisClientes` (rol Comercial): filtro de vista "Mis clientes"; acota filas,
 * totales, gráficos y títulos A3 a sus cuentas del SGC.
 *
 * `soloLectura` (rol Comercial): sin las herramientas de la mesa (traer o cargar
 * saldos del custodio, movimientos A3, recalcular), que las reglas de Firestore
 * tampoco le permiten. Las acreditaciones esperadas sí las resuelve, de cualquier
 * comitente (igual que la mesa).
 */
const ControlSaldosTab = ({
  clientesDb, especies, onPrecargarCaucion, filtroMisClientes = null, soloLectura = false,
}) => {
  const scopeMisClientes = filtroMisClientes ? filtroMisClientes.comitentes : null;
  const {
    fechaHoy,
    initialSaldos,
    allCauciones,
    allPases,
    allOperaciones,
    instruccionesDinero,
    acreditaciones,
    debitosLicitaciones,
    snapshotsHoy,
    snapshotActivo,
    tieneSnapshotInicialHoy,
    isLoadingSaldos,
    saldosPorBucket,
    esModoProductor,
  } = useSaldosClientes();
  const puedeOperarSaldos = !esModoProductor && !soloLectura;
  const puedeMarcarAcreditaciones = !esModoProductor;

  const currentUser = auth.currentUser;
  const custodioMeta = useSaldosCustodioMeta(fechaHoy, !esModoProductor);
  const [isCargandoCustodio, setIsCargandoCustodio] = useState(false);

  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'saldoActual', direction: 'descending' });
  const [clienteFilter, setClienteFilter] = useState('');
  const [activeSubTab, setActiveSubTab] = useState(BUCKET_ARS);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [isA3MovementModalOpen, setIsA3MovementModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [sortConfigA3, setSortConfigA3] = useState({ key: 'nroComitente', direction: 'ascending' });

  const [isUploadSnapshotModalOpen, setIsUploadSnapshotModalOpen] = useState(false);
  const [pendingSnapshotTipo, setPendingSnapshotTipo] = useState(null);
  const [desgloseModal, setDesgloseModal] = useState({ isOpen: false, cuenta: null });
  const [conciliacionResultado, setConciliacionResultado] = useState(null);

  const isLoading = isLoadingSaldos;

  // Las sub-pestañas que no son de moneda (A3, Snapshots) caen en pesos: no
  // renderizan esta tabla, pero el memo se evalúa igual.
  // Acreditaciones esperadas del día en la moneda activa, por comitente. La columna
  // muestra lo esperado; solo lo marcado 'Acreditada' entra al saldo (como movimiento
  // ejecutado, desde calculateSaldos).
  const acreditacionesBucket = useMemo(
    () => resumirPorBucket(acreditaciones.porComitente, activeSubTab),
    [acreditaciones.porComitente, activeSubTab],
  );

  const calculatedData = useMemo(() => {
    const resultMap = saldosPorBucket[activeSubTab] || saldosPorBucket[BUCKET_ARS];
    return filterByComitentes(toArrayConSaldo(resultMap), scopeMisClientes).map(d => {
      const esperada = acreditacionesBucket.get(d.nroComitente);
      return {
        ...d,
        saldoInicial: d.saldoSnapshot,
        saldoActual: d.saldoReal,
        movimientos: d.saldoReal - d.saldoSnapshot,
        movimientosCargados: d.saldoProyectado - d.saldoReal,
        acreditacionEsperada: esperada ? esperada.pendiente.total : 0,
        acreditacionResumen: esperada || null,
      };
    });
  }, [activeSubTab, saldosPorBucket, acreditacionesBucket, scopeMisClientes]);

  const acreditacionesSinFila = useMemo(() => {
    const conFila = new Set(calculatedData.map(d => d.nroComitente));
    return filterByComitentes(Array.from(acreditacionesBucket.values()), scopeMisClientes)
      .filter(r => !conFila.has(r.nroComitente));
  }, [calculatedData, acreditacionesBucket, scopeMisClientes]);

  // La cuenta del desglose se toma de los datos vivos: al marcar una acreditación
  // dentro del modal, el saldo tiene que moverse sin cerrarlo y volver a abrirlo.
  const cuentaDesglose = useMemo(() => {
    const abierta = desgloseModal.cuenta;
    if (!abierta) return null;
    return calculatedData.find(d => d.nroComitente === abierta.nroComitente) || abierta;
  }, [desgloseModal.cuenta, calculatedData]);

  const acreditacionesDisponibles = !acreditaciones.error && !!acreditaciones.meta && !acreditaciones.meta.error;

  const handleMarcarAcreditacion = useCallback(async (evento, nroComitente, estado, montoReal) => {
    try {
      await marcarAcreditacion(currentUser, evento, nroComitente, estado, montoReal);
    } catch (error) {
      alert('No se pudo guardar la marca de la acreditación. Probá de nuevo.');
    }
  }, [currentUser]);

  const handleCargarDesdeCustodio = useCallback(async () => {
    setIsCargandoCustodio(true);
    try {
      const result = await solicitarCargaSaldosCustodio(currentUser);
      if (result.estado === 'omitida') {
        alert('Ya hay un snapshot inicial de hoy: no se volvió a cargar. Para actualizar usá la carga intradiaria del Excel.');
      }
    } catch (error) {
      alert(`No se pudieron traer los saldos de Rosval: ${error.message}
Podés cargar el Excel a mano.`);
    } finally {
      setIsCargandoCustodio(false);
    }
  }, [currentUser]);

  const handleRecalcularAcreditaciones = useCallback(async () => {
    try {
      await solicitarRecalculoAcreditaciones(currentUser);
    } catch (error) {
      alert('No se pudieron recalcular las acreditaciones esperadas contra el SGC.');
    }
  }, [currentUser]);

  // Comitente con eventos pero sin fila en la tabla (no tiene saldo en esta
  // moneda): el desglose se abre igual, con saldos en cero.
  const abrirDesgloseSinFila = useCallback((resumen) => {
    setDesgloseModal({
      isOpen: true,
      cuenta: {
        nroComitente: resumen.nroComitente,
        nombre: resumen.nombre,
        saldoSnapshot: 0,
        saldoReal: 0,
        saldoProyectado: 0,
        movimientosReales: [],
        movimientosPendientes: [],
      },
    });
  }, []);

  const sortedCalculatedData = useMemo(() => {
    let sortableItems = [...calculatedData];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        let comparison = 0;
        if (typeof valA === 'string' && typeof valB === 'string') {
          comparison = valA.localeCompare(valB);
        } else {
          comparison = (valA > valB) ? 1 : (valA < valB) ? -1 : 0;
        }
        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }
    return sortableItems;
  }, [calculatedData, sortConfig]);

  const filteredCalculatedData = useMemo(() => {
    const term = clienteFilter.trim().toLowerCase();
    if (!term) return sortedCalculatedData;
    return sortedCalculatedData.filter(d => d.nombre?.toLowerCase().includes(term));
  }, [sortedCalculatedData, clienteFilter]);

  const requestSort = (key) => {
    let direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
    setSortConfig({ key, direction });
  };

  // Chart and Indicator Data Preparation
  const { chartData, totalAcreedor, totalDeudor } = useMemo(() => {
    const acreedores = { labels: [], data: [] };
    const deudores = { labels: [], data: [] };
    let totalAcreedor = 0;
    let totalDeudor = 0;

    sortedCalculatedData.forEach(d => {
      if (d.saldoActual > 0) {
        acreedores.labels.push(d.nombre);
        acreedores.data.push(d.saldoActual);
        totalAcreedor += d.saldoActual;
      } else if (d.saldoActual < 0) {
        deudores.labels.push(d.nombre);
        deudores.data.push(Math.abs(d.saldoActual));
        totalDeudor += d.saldoActual;
      }
    });

    const pieColors = (count, baseColor) => Array.from({ length: count }, (_, i) => `hsl(${baseColor}, 55%, ${75 - i * 6}%)`);

    return {
      chartData: {
        acreedores: {
          labels: acreedores.labels,
          datasets: [{ data: acreedores.data, backgroundColor: pieColors(acreedores.data.length, 120), borderColor: theme.colors.surface, borderWidth: 2 }]
        },
        deudores: {
          labels: deudores.labels,
          datasets: [{ data: deudores.data, backgroundColor: pieColors(deudores.data.length, 0), borderColor: theme.colors.surface, borderWidth: 2 }]
        }
      },
      totalAcreedor,
      totalDeudor
    };
  }, [sortedCalculatedData]);

  // Preparar datos de títulos A3
  const titulosA3Data = useMemo(() => {
    const titulos = [];
    filterByComitentes(initialSaldos, scopeMisClientes).forEach(saldo => {
      if (saldo.titulosA3 && Array.isArray(saldo.titulosA3) && saldo.titulosA3.length > 0) {
        saldo.titulosA3.forEach(titulo => {
          titulos.push({
            nroComitente: saldo.nroComitente,
            nombreCliente: saldo.nombre,
            codigoEspecie: titulo.codigoEspecie,
            nombreEspecie: titulo.nombreEspecie,
            cantidad: titulo.cantidad
          });
        });
      }
    });
    return titulos;
  }, [initialSaldos, scopeMisClientes]);

  // Ordenar títulos A3
  const sortedTitulosA3 = useMemo(() => {
    let sortableItems = [...titulosA3Data];
    if (sortConfigA3 !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfigA3.key];
        const valB = b[sortConfigA3.key];
        let comparison = 0;
        if (typeof valA === 'string' && typeof valB === 'string') {
          comparison = valA.localeCompare(valB);
        } else {
          comparison = (valA > valB) ? 1 : (valA < valB) ? -1 : 0;
        }
        return sortConfigA3.direction === 'ascending' ? comparison : -comparison;
      });
    }
    return sortableItems;
  }, [titulosA3Data, sortConfigA3]);

  const filteredTitulosA3 = useMemo(() => {
    const term = clienteFilter.trim().toLowerCase();
    if (!term) return sortedTitulosA3;
    return sortedTitulosA3.filter(t => t.nombreCliente?.toLowerCase().includes(term));
  }, [sortedTitulosA3, clienteFilter]);

  const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
    useHorizontalScrollSync([filteredCalculatedData, filteredTitulosA3, activeSubTab, isLoading]);

  const requestSortA3 = (key) => {
    let direction = sortConfigA3.key === key && sortConfigA3.direction === 'ascending' ? 'descending' : 'ascending';
    setSortConfigA3({ key, direction });
  };

  // Función para sanitizar IDs de Firestore (no permite /, //, ., ..)
  const sanitizeFirestoreId = (id) => {
    return id
      .replace(/\//g, '-')  // Reemplazar / por -
      .replace(/\./g, '_')  // Reemplazar . por _
      .trim();
  };

  // Función para manejar el movimiento de títulos A3
  const handleA3MovementSubmit = async (movementData) => {
    try {
      const { operacion, nroComitente, especie, vn } = movementData;

      // Buscar el documento del cliente en la colección saldos
      const saldoDocRef = doc(db, "saldos", nroComitente);
      const saldoDoc = await getDoc(saldoDocRef);

      if (!saldoDoc.exists()) {
        throw new Error('Cliente no encontrado en los saldos');
      }

      // Obtener la especie completa para obtener el código
      const especieObj = especies.find(esp => esp.nombre === especie);
      if (!especieObj) {
        throw new Error('Especie no encontrada');
      }

      const saldoData = saldoDoc.data();
      const titulosA3 = saldoData.titulosA3 || [];

      // Buscar si ya existe este título para este cliente
      const tituloExistenteIndex = titulosA3.findIndex(
        t => t.nombreEspecie === especie
      );

      let nuevosTitulosA3;

      if (operacion === 'Alta') {
        // Si existe, sumar la cantidad
        if (tituloExistenteIndex !== -1) {
          nuevosTitulosA3 = [...titulosA3];
          nuevosTitulosA3[tituloExistenteIndex].cantidad += vn;
        } else {
          // Si no existe, agregar nuevo
          nuevosTitulosA3 = [
            ...titulosA3,
            {
              codigoEspecie: especieObj.codigo,
              nombreEspecie: especieObj.nombre,
              cantidad: vn
            }
          ];
        }
      } else { // Baja
        if (tituloExistenteIndex === -1) {
          throw new Error('No se puede dar de baja un título que no existe');
        }

        nuevosTitulosA3 = [...titulosA3];
        const nuevaCantidad = nuevosTitulosA3[tituloExistenteIndex].cantidad - vn;

        if (nuevaCantidad < 0) {
          throw new Error('La cantidad a dar de baja es mayor a la cantidad disponible');
        }

        if (nuevaCantidad === 0) {
          // Eliminar el título si la cantidad llega a 0
          nuevosTitulosA3.splice(tituloExistenteIndex, 1);
        } else {
          nuevosTitulosA3[tituloExistenteIndex].cantidad = nuevaCantidad;
        }
      }

      // Actualizar el documento
      await updateDoc(saldoDocRef, {
        titulosA3: nuevosTitulosA3,
        ultimaActualizacionA3: serverTimestamp()
      });

      alert(`Movimiento de ${operacion} registrado exitosamente`);
      setIsA3MovementModalOpen(false);

    } catch (error) {
      console.error("Error al procesar movimiento A3:", error);
      throw error;
    }
  };

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsProcessingFile(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const batch = writeBatch(db);
        const saldosMap = new Map();
        const titulosA3Map = new Map(); // Mapa para títulos A3 por comitente
        const especiesSet = new Map(); // Set para especies únicas

        for (let i = 2; i < json.length; i++) {
          const row = json[i];
          const mercado = row[0]; // Columna A (índice 0)

          // Procesar SALDOS (DIS). La columna "Unidad" trae ARS, USD (MEP) y
          // USDC (cable); cualquier otra unidad se ignora. Solo el uso GRAL es
          // disponible: MAR son fondos afectados a márgenes (mismo criterio que
          // functions/saldosCustodio.js).
          if (row[2] === 'DIS' && String(row[1] ?? '').trim().toUpperCase() === 'GRAL') {
            const bucket = bucketDeUnidadCustodio(row[3]);
            const clientString = row[4];
            const saldo = row[5];

            if (clientString && typeof clientString === 'string' && bucket) {
              const match = clientString.match(/\[(\d{9})\]/);
              if (match) {
                const nroComitente = match[1];
                const nombre = clientString.replace(match[0], '').trim();
                const saldoInicial = typeof saldo === 'number' ? saldo * -1 : 0;

                if (!saldosMap.has(nroComitente)) {
                  saldosMap.set(nroComitente, { nroComitente, nombre, ...saldosInicialesVacios(), titulosA3: [] });
                }

                const clientData = saldosMap.get(nroComitente);
                clientData[SALDO_INICIAL_FIELD[bucket]] += saldoInicial;
              }
            }
          }

          // Procesar TÍTULOS A3
          if (mercado === 'A3') {
            const especieString = row[3]; // Columna D (índice 3)
            const clientString = row[4]; // Columna E (índice 4)
            const cantidad = row[5]; // Columna F (índice 5)

            if (especieString && clientString && typeof cantidad === 'number') {
              // Extraer código de especie: [8549] SPY - CEDEAR SPDR S&P 500
              const especieMatch = especieString.match(/\[(\d+)\]\s*(.+)/);
              // Extraer número de comitente: [568000005] Valentin Viotti
              const clientMatch = clientString.match(/\[(\d{9})\]\s*(.+)/);

              if (especieMatch && clientMatch) {
                const codigoEspecie = especieMatch[1];
                const nombreEspecie = especieMatch[2].trim();
                const nroComitente = clientMatch[1];
                const nombreCliente = clientMatch[2].trim();
                const cantidadReal = cantidad * -1; // Invertir el signo

                // Guardar especie en el set usando el nombre como ID
                if (!especiesSet.has(nombreEspecie)) {
                  especiesSet.set(nombreEspecie, {
                    codigo: codigoEspecie,
                    nombre: nombreEspecie,
                    moneda: 'Pesos' // Valor por defecto, puede ajustarse según lógica
                  });
                }

                // Guardar título A3
                if (!titulosA3Map.has(nroComitente)) {
                  titulosA3Map.set(nroComitente, {
                    nroComitente,
                    nombre: nombreCliente,
                    titulos: []
                  });
                }

                titulosA3Map.get(nroComitente).titulos.push({
                  codigoEspecie,
                  nombreEspecie,
                  cantidad: cantidadReal
                });
              }
            }
          }
        }

        // Merge títulos A3 con saldos
        titulosA3Map.forEach((data, nroComitente) => {
          if (!saldosMap.has(nroComitente)) {
            saldosMap.set(nroComitente, {
              nroComitente: data.nroComitente,
              nombre: data.nombre,
              ...saldosInicialesVacios(),
              titulosA3: data.titulos
            });
          } else {
            saldosMap.get(nroComitente).titulosA3 = data.titulos;
          }
        });

        if (saldosMap.size === 0 && especiesSet.size === 0) {
          alert("No se encontraron clientes, saldos o especies válidas en el archivo.");
        } else {
          // Guardar saldos y clientes (compat legacy con coleccion `saldos`)
          saldosMap.forEach(saldoData => {
            const saldoRef = doc(db, "saldos", saldoData.nroComitente);
            batch.set(saldoRef, saldoData, { merge: true });
            // El padrón se indexa por comitente y no por nombre: preguntar por
            // `clientesDb[nro]` daba falsy para un cliente ya cargado sin nombre y
            // lo volvía a crear en cada subida del archivo. El id determinístico
            // cierra además la ventana de carrera con el snapshot del padrón:
            // dos subidas escriben el mismo documento en vez de dos.
            if (!Object.prototype.hasOwnProperty.call(clientesDb, saldoData.nroComitente)) {
              const newClientRef = doc(db, "clientes", saldoData.nroComitente);
              batch.set(
                newClientRef,
                { nroComitente: saldoData.nroComitente, nombre: saldoData.nombre },
                { merge: true }
              );
            }
          });

          // Guardar especies
          especiesSet.forEach(especieData => {
            // Sanitizar el nombre para usarlo como ID de Firestore
            const sanitizedId = sanitizeFirestoreId(especieData.nombre);
            const especieRef = doc(db, "especies", sanitizedId);
            batch.set(especieRef, especieData, { merge: true });
          });

          await batch.commit();

          // Crear snapshot histórico (no sobreescribe — cada upload crea un doc nuevo)
          const tipoSnapshot = pendingSnapshotTipo || SNAPSHOT_TIPO.INICIAL;
          const comitentesSnapshot = Array.from(saldosMap.values()).map(s => ({
            nroComitente: s.nroComitente,
            nombre: s.nombre,
            ...MONEDA_BUCKETS.reduce((acc, bucket) => ({
              ...acc,
              [SNAPSHOT_SALDO_FIELD[bucket]]: Number(s[SALDO_INICIAL_FIELD[bucket]]) || 0,
            }), {}),
            titulosA3: Array.isArray(s.titulosA3) ? s.titulosA3 : [],
          }));
          const currentUser = auth.currentUser;
          const uploadedBy = currentUser ? {
            uid: currentUser.uid,
            email: currentUser.email || '',
            nombre: currentUser.displayName || '',
          } : null;
          const snapshotNuevoId = await crearSnapshotSaldos({
            fecha: getFechaSnapshotKey(),
            tipo: tipoSnapshot,
            comitentes: comitentesSnapshot,
            uploadedBy,
          });

          const mensajes = [];
          if (saldosMap.size > 0) mensajes.push(`${saldosMap.size} clientes`);
          if (titulosA3Map.size > 0) mensajes.push(`${titulosA3Map.size} clientes con títulos A3`);
          if (especiesSet.size > 0) mensajes.push(`${especiesSet.size} especies`);
          mensajes.push(`snapshot ${tipoSnapshot} guardado`);

          alert(`Carga exitosa:\n- ${mensajes.join('\n- ')}`);

          // Conciliación automática solo si es intradiario y existe un snapshot anterior del día
          if (tipoSnapshot === SNAPSHOT_TIPO.INTRADIARIO && snapshotsHoy.length > 0) {
            const snapshotAnterior = snapshotsHoy[snapshotsHoy.length - 1];
            const snapshotNuevoLocal = {
              tipo: tipoSnapshot,
              uploadedAt: new Date(),
              comitentes: comitentesSnapshot,
            };
            const diferencias = conciliarSnapshots({
              snapshotAnterior,
              snapshotNuevo: snapshotNuevoLocal,
              cauciones: allCauciones,
              pases: allPases,
              operaciones: allOperaciones,
              instrucciones: instruccionesDinero,
              acreditaciones: acreditaciones.porComitente,
            });
            try {
              await guardarResultadoConciliacion(snapshotNuevoId, diferencias);
            } catch (errPersist) {
              console.warn('No se pudo persistir el resultado de conciliación:', errPersist);
            }

            // Sugerencias: rescates T+1 que liquidan hoy, agrupados por comitente y moneda
            const fechaHoy = toIsoDate(new Date());
            const rescatesT1Hoy = instruccionesDinero.filter(i =>
              i.tipo === 'Rescate FCI'
              && i.plazoLiquidacion === 'T+1'
              && i.estado === 'Hecha'
              && i.fechaLiquidacion === fechaHoy
            );
            const groupByComitente = (lista) => lista.reduce((acc, i) => {
              if (!acc[i.nroComitente]) acc[i.nroComitente] = [];
              acc[i.nroComitente].push({ monto: Number(i.monto) || 0, fci: i.fci });
              return acc;
            }, {});

            setConciliacionResultado({
              diferencias,
              totalCuentas: comitentesSnapshot.length,
              rescatesT1PorComitente: MONEDA_BUCKETS.reduce((acc, bucket) => ({
                ...acc,
                [bucket]: groupByComitente(
                  rescatesT1Hoy.filter(i => i.moneda === MONEDA_MOVIMIENTO[bucket]),
                ),
              }), {}),
            });
          }
        }
      } catch (error) {
        console.error("Error procesando el archivo:", error);
        alert("Ocurrió un error al procesar el archivo.");
      } finally {
        setIsProcessingFile(false);
        setPendingSnapshotTipo(null);
        e.target.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  }, [
    clientesDb, pendingSnapshotTipo, snapshotsHoy, allCauciones, allPases, allOperaciones,
    instruccionesDinero, acreditaciones.porComitente,
  ]);

  const getRowStyle = (saldo) => {
    if (saldo > 0) return { backgroundColor: theme.colors.success };
    if (saldo < 0) return { backgroundColor: theme.colors.error };
    return {};
  };

  const handleSaldoCellClick = useCallback((e, d, saldo) => {
    e.stopPropagation();
    if (!saldo || !onPrecargarCaucion) return;
    onPrecargarCaucion({
      nroComitente: d.nroComitente,
      nombre: d.nombre,
      moneda: MONEDA_MOVIMIENTO[activeSubTab],
      saldo,
    });
  }, [activeSubTab, onPrecargarCaucion]);

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${formatSaldo(c.parsed, activeSubTab)}` } },
      datalabels: {
        display: (context) => (context.dataset.data[context.dataIndex] / context.chart.getDatasetMeta(0).total) * 100 > 4,
        color: '#1f2937',
        borderRadius: 3,
        font: { weight: '600', size: 11 },
        formatter: (value, context) => context.chart.data.labels[context.dataIndex],
        padding: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
      }
    }
  };

  const handleInstructionsContinue = () => {
    setIsUploadSnapshotModalOpen(true);
  };

  const handleSnapshotTipoConfirm = (tipo) => {
    setPendingSnapshotTipo(tipo);
    setIsUploadSnapshotModalOpen(false);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  return (
    <TabWrapper>
      <Header className="axon-wrap" style={{ gap: '0.75rem' }}>
        <Title className="axon-page-title">Control de Saldos</Title>
        <ActionsContainer className="axon-wrap">
          {snapshotActivo && (
            <SnapshotBadge>
              Snapshot activo: <strong>{snapshotActivo.tipo}</strong> @ {formatSnapshotHora(snapshotActivo.uploadedAt)}
              {!esModoProductor && <SnapshotBadgeCount>{snapshotsHoy.length} hoy</SnapshotBadgeCount>}
            </SnapshotBadge>
          )}
          {puedeOperarSaldos && (
            <>
              {!tieneSnapshotInicialHoy && (
                <Button onClick={handleCargarDesdeCustodio} disabled={isCargandoCustodio || isProcessingFile}>
                  {isCargandoCustodio ? 'Trayendo de Rosval...' : 'Traer saldos de Rosval'}
                </Button>
              )}
              <Button onClick={() => setIsInstructionsModalOpen(true)}>
                {isProcessingFile ? 'Procesando...' : 'Cargar Saldos (.xlsx)'}
              </Button>
              <HiddenInput
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx"
                disabled={isProcessingFile}
              />
            </>
          )}
        </ActionsContainer>
      </Header>

      {!esModoProductor && !tieneSnapshotInicialHoy && custodioMeta?.estado === 'error' && (
        <CustodioError>
          Falló la carga automática de saldos desde Rosval: {custodioMeta.error} Probá "Traer saldos de
          Rosval" o cargá el Excel a mano.
        </CustodioError>
      )}

      {!isLoadingSaldos && !tieneSnapshotInicialHoy && initialSaldos.length > 0 && (
        <StaleWarning>
          Todavía no se cargó el snapshot del custodio de hoy ({formatDate(fromIsoDate(fechaHoy))}).
          La tabla muestra los últimos saldos conocidos más los movimientos del día, no el saldo
          real del custodio: {esModoProductor
            ? 'se completa con la carga de las 09:30.'
            : 'se carga solo a las 09:30 desde Rosval, o traelo ahora con el botón.'}
        </StaleWarning>
      )}

      {debitosLicitaciones.ordenesSinTipoCambio > 0 && (
        <StaleWarning>
          {debitosLicitaciones.ordenesSinTipoCambio === 1
            ? 'Hay 1 orden adjudicada en día de caja que no se descuenta del saldo'
            : `Hay ${debitosLicitaciones.ordenesSinTipoCambio} órdenes adjudicadas en día de caja que no se descuentan del saldo`}
          : son de una especie en USD que se integra en pesos y la licitación no tiene el tipo de
          cambio cargado. Cargalo editando la licitación.
        </StaleWarning>
      )}

      {!esModoProductor && (
        <>
          <InstructionsModal
            isOpen={isInstructionsModalOpen}
            onClose={() => setIsInstructionsModalOpen(false)}
            onContinue={handleInstructionsContinue}
          />

          <UploadSnapshotModal
            isOpen={isUploadSnapshotModalOpen}
            onClose={() => setIsUploadSnapshotModalOpen(false)}
            onConfirm={handleSnapshotTipoConfirm}
            snapshotsDelDia={snapshotsHoy}
          />
        </>
      )}

      <SubTabContainer className="axon-scroll-x">
        {MONEDA_BUCKETS.map(bucket => (
          <SubTabButton
            key={bucket}
            $active={activeSubTab === bucket}
            onClick={() => setActiveSubTab(bucket)}
          >
            {MONEDA_TITULO[bucket]}
          </SubTabButton>
        ))}
        <SubTabButton $active={activeSubTab === 'A3'} onClick={() => setActiveSubTab('A3')}>Títulos A3</SubTabButton>
        {!esModoProductor && (
          <SubTabButton $active={activeSubTab === 'SNAPSHOTS'} onClick={() => setActiveSubTab('SNAPSHOTS')}>
            Snapshots {snapshotsHoy.length > 0 && `(${snapshotsHoy.length})`}
          </SubTabButton>
        )}
      </SubTabContainer>

      {isLoading ? (<Placeholder>Cargando datos...</Placeholder>) :
        activeSubTab === 'SNAPSHOTS' ? (
          <SnapshotsHistorial
            snapshots={snapshotsHoy}
            onVerConciliacion={(snap) => {
              if (!snap.conciliacion) return;
              setConciliacionResultado({
                diferencias: MONEDA_BUCKETS.reduce((acc, bucket) => ({
                  ...acc,
                  [bucket]: snap.conciliacion[DIFERENCIAS_FIELD[bucket]] || [],
                }), {}),
                totalCuentas: snap.totalCuentas || (snap.comitentes?.length || 0),
                rescatesT1PorComitente: {},
              });
            }}
          />
        ) :
        !snapshotActivo && !initialSaldos.length ? (
          <Placeholder>
            {!puedeOperarSaldos
              ? 'Todavía no hay saldos cargados para hoy. Se cargan a las 09:30 de cada día hábil: después vas a ver acá los saldos de tus clientes.'
              : 'Todavía no hay saldos de hoy. Se cargan automáticamente desde Rosval a las 09:30 (días hábiles); podés traerlos antes con "Traer saldos de Rosval" o cargar el Excel.'}
          </Placeholder>
        ) :
          activeSubTab === 'A3' ? (
            // Vista de Títulos A3
            <>
              {puedeOperarSaldos && (
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setIsA3MovementModalOpen(true)}>
                    + Cargar Movimiento
                  </Button>
                </div>
              )}
              {titulosA3Data.length === 0 ? (
                <Placeholder>No hay títulos en A3 para mostrar.</Placeholder>
              ) : (
                <>
                <SearchInput
                  className="axon-fill"
                  type="text"
                  placeholder="Buscar cliente..."
                  value={clienteFilter}
                  onChange={e => setClienteFilter(e.target.value)}
                />
                <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />
                <TableContainer ref={contentRef} onScroll={onContentScroll}>
                  <StyledTable as={StackedTable} stack={A3_STACK}>
                    <thead>
                      <tr>
                        <th onClick={() => requestSortA3('nroComitente')}>
                          Nro Comitente {sortConfigA3.key === 'nroComitente' && <SortIcon direction={sortConfigA3.direction} />}
                        </th>
                        <th onClick={() => requestSortA3('nombreCliente')}>
                          Cliente {sortConfigA3.key === 'nombreCliente' && <SortIcon direction={sortConfigA3.direction} />}
                        </th>
                        <th onClick={() => requestSortA3('codigoEspecie')}>
                          Código {sortConfigA3.key === 'codigoEspecie' && <SortIcon direction={sortConfigA3.direction} />}
                        </th>
                        <th onClick={() => requestSortA3('nombreEspecie')}>
                          Especie {sortConfigA3.key === 'nombreEspecie' && <SortIcon direction={sortConfigA3.direction} />}
                        </th>
                        <th onClick={() => requestSortA3('cantidad')} style={{ textAlign: 'right' }}>
                          Cantidad (VN) {sortConfigA3.key === 'cantidad' && <SortIcon direction={sortConfigA3.direction} />}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTitulosA3.map((titulo, index) => (
                        <tr key={`${titulo.nroComitente}-${titulo.codigoEspecie}-${index}`}>
                          <td>{titulo.nroComitente}</td>
                          <td>{titulo.nombreCliente}</td>
                          <td>{titulo.codigoEspecie}</td>
                          <td>{titulo.nombreEspecie}</td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{titulo.cantidad.toLocaleString('es-AR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>
                </>
              )}
            </>
          ) :
            (
              // Vista de Saldos (Pesos, Dólares o USD Cable)
              <>
                <IndicatorsContainer className="axon-grid-2">
                  <IndicatorCard title="Total Saldos Acreedores" value={totalAcreedor} currency={activeSubTab} color={theme.colors.success.replace('0.15', '1')} compactMobile />
                  <IndicatorCard title="Total Saldos Deudores" value={totalDeudor} currency={activeSubTab} color={theme.colors.errorText} compactMobile />
                </IndicatorsContainer>
                <MobileCollapsibleSection title="Gráficos" summary="Detalle de acreedores y deudores">
                <ChartContainer>
                  <ChartWrapper>
                    <h3>Detalle Saldos Acreedores</h3>
                    <DoughnutWrapper>
                      {chartData.acreedores.datasets[0].data.length > 0 ? <Doughnut data={chartData.acreedores} options={pieOptions} /> : <p>Sin saldos acreedores.</p>}
                    </DoughnutWrapper>
                  </ChartWrapper>
                  <ChartWrapper>
                    <h3>Detalle Saldos Deudores</h3>
                    <DoughnutWrapper>
                      {chartData.deudores.datasets[0].data.length > 0 ? <Doughnut data={chartData.deudores} options={pieOptions} /> : <p>Sin saldos deudores.</p>}
                    </DoughnutWrapper>
                  </ChartWrapper>
                </ChartContainer>
                </MobileCollapsibleSection>
                <AcreditacionesEsperadasAviso
                  moneda={activeSubTab}
                  meta={acreditaciones.meta}
                  isLoading={acreditaciones.isLoading}
                  error={acreditaciones.error}
                  resumen={acreditacionesBucket}
                  sinFila={acreditacionesSinFila}
                  puedeRecalcular={puedeOperarSaldos}
                  onRecalcular={handleRecalcularAcreditaciones}
                  onVerComitente={abrirDesgloseSinFila}
                />
                <SearchInput
                  className="axon-fill"
                  type="text"
                  placeholder="Buscar cliente..."
                  value={clienteFilter}
                  onChange={e => setClienteFilter(e.target.value)}
                />
                <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />
                <TableContainer ref={contentRef} onScroll={onContentScroll}>
                  <StyledTable as={StackedTable} stack={SALDOS_STACK}>
                    <thead>
                      <tr>
                        <th onClick={() => requestSort('nroComitente')}>Nro Comitente {sortConfig.key === 'nroComitente' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th onClick={() => requestSort('nombre')}>Nombre Cliente {sortConfig.key === 'nombre' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th onClick={() => requestSort('saldoSnapshot')} style={{ textAlign: 'right' }}>Saldo Inicial {sortConfig.key === 'saldoSnapshot' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th onClick={() => requestSort('movimientos')} style={{ textAlign: 'right' }}>Mov. Ejecutados {sortConfig.key === 'movimientos' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th onClick={() => requestSort('saldoReal')} style={{ textAlign: 'right' }}>Saldo Real {sortConfig.key === 'saldoReal' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th
                          onClick={() => requestSort('acreditacionEsperada')}
                          style={{ textAlign: 'right' }}
                          title="Cupones, amortizaciones, vencimientos de letras y cheques que vencen hoy según la cartera del SGC, pendientes de corroborar. Al marcarlos Acreditada pasan al Saldo Real y al Saldo Final, y acá queda el importe acreditado."
                        >Acred. Esperada {sortConfig.key === 'acreditacionEsperada' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th onClick={() => requestSort('movimientosCargados')} style={{ textAlign: 'right' }}>Mov. Pendientes {sortConfig.key === 'movimientosCargados' && <SortIcon direction={sortConfig.direction} />}</th>
                        <th onClick={() => requestSort('saldoProyectado')} style={{ textAlign: 'right' }}>Saldo Final {sortConfig.key === 'saldoProyectado' && <SortIcon direction={sortConfig.direction} />}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCalculatedData.map(d => (
                        <tr
                          key={d.nroComitente}
                          style={{ ...getRowStyle(d.saldoActual), cursor: 'pointer' }}
                          onClick={() => setDesgloseModal({ isOpen: true, cuenta: d })}
                          title="Click para ver desglose"
                        >
                          <td>{d.nroComitente}</td>
                          <td>{d.nombre}</td>
                          <td style={{ textAlign: 'right' }}>{formatSaldo(d.saldoSnapshot, activeSubTab)}</td>
                          <td
                            style={{ textAlign: 'right', color: colorMovimiento(d.movimientos) }}
                            title={detalleMovimientosEjecutados(d.movimientosReales, activeSubTab)}
                          >{formatSaldo(d.movimientos, activeSubTab)}</td>
                          <td
                            style={{ textAlign: 'right', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline dotted' }}
                            onClick={(e) => handleSaldoCellClick(e, d, d.saldoReal)}
                            title="Click para precargar caución (Tomadora si es deudor, Colocadora si es acreedor)"
                          >{formatSaldo(d.saldoReal, activeSubTab)}</td>
                          <AcreditacionEsperadaCell resumen={d.acreditacionResumen} moneda={activeSubTab} />
                          <td style={{ textAlign: 'right', color: colorMovimiento(d.movimientosCargados) }}>{formatSaldo(d.movimientosCargados, activeSubTab)}</td>
                          <td
                            style={{ textAlign: 'right', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline dotted', fontStyle: d.saldoReal !== d.saldoProyectado ? 'italic' : 'normal', color: d.saldoReal !== d.saldoProyectado ? '#B45309' : undefined }}
                            onClick={(e) => handleSaldoCellClick(e, d, d.saldoProyectado)}
                            title="Click para precargar caución (Tomadora si es deudor, Colocadora si es acreedor)"
                          >{formatSaldo(d.saldoProyectado, activeSubTab)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StyledTable>
                </TableContainer>
              </>
            )}

      {!esModoProductor && (
        <AddA3MovementModal
          isOpen={isA3MovementModalOpen}
          onClose={() => setIsA3MovementModalOpen(false)}
          onSubmit={handleA3MovementSubmit}
          clientes={Object.entries(clientesDb).map(([nroComitente, nombre]) => ({
            nroComitente,
            nombre
          }))}
          especies={especies || []}
        />
      )}

      <SaldoDesgloseModal
        isOpen={desgloseModal.isOpen}
        onClose={() => setDesgloseModal({ isOpen: false, cuenta: null })}
        cuenta={cuentaDesglose}
        moneda={activeSubTab}
        snapshotInfo={snapshotActivo}
        acreditaciones={desgloseModal.cuenta && acreditacionesDisponibles
          ? (acreditacionesBucket.get(desgloseModal.cuenta.nroComitente)
            || { eventos: [], fechaCartera: acreditaciones.meta.fechaCartera })
          : null}
        puedeMarcarAcreditaciones={!!desgloseModal.cuenta && puedeMarcarAcreditaciones}
        onMarcarAcreditacion={(evento, estado, montoReal) =>
          handleMarcarAcreditacion(evento, desgloseModal.cuenta.nroComitente, estado, montoReal)}
      />

      {!esModoProductor && (
        <ConciliacionResultadoModal
          isOpen={!!conciliacionResultado}
          onClose={() => setConciliacionResultado(null)}
          diferencias={conciliacionResultado?.diferencias}
          totalCuentas={conciliacionResultado?.totalCuentas}
          rescatesT1PorComitente={conciliacionResultado?.rescatesT1PorComitente}
        />
      )}
    </TabWrapper>
  );
};

export default ControlSaldosTab;